
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Collapsible, CollapsibleContent, } from './ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Order } from '../../../types/Order.interface';
import { useScreenDetector } from '../../../hooks/useScreenDetector';
import { getStatusDescription } from '../../../utils/getOrderStatusDescription';
import Preloader from '../../../ui/Loader/Preloader';

interface OrderHistoryProps {
  orders: Order[];
  getOrders: (page: number, limit: number) => void;
  formatDate: (date: string) => string;
  getPaymentStatus: (order: Order) => string;
  copyOrder: (orderId: number) => void;
  isLoading: boolean;
}

const OrderHistory = ({ orders, getOrders, formatDate, getPaymentStatus, copyOrder, isLoading  }: OrderHistoryProps) => {
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const {isMobile} = useScreenDetector();
  
  const toggleOrderDetails = (orderId: number) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
    } else {
      setExpandedOrder(orderId);
    }
  };

  // Format the date from YYYY-MM-DD to DD.MM.YYYY
  useEffect(() => {
    getOrders(0, 10);
  }, []);

  if(isLoading) return <Preloader />;
  
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">История заказов</h2>
      
      {isMobile ? (
        // Mobile view
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.orderId} className="rounded-lg bg-white shadow overflow-hidden">
              <div className="p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Дата</span>
                  <span>{formatDate(order.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Номер</span>
                  <span className="text-autoparts-orange font-medium"><a href={`/m/${order.orderHash}`}>{order.orderNumber}</a></span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Статус</span>
                  <span className={`${order.status === 'canceled' ? 'text-red-500' : 'text-green-600'}`}>{getStatusDescription(order, false)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Сумма</span>
                  <span>{order.status !== 'canceled' ? order.priceAmount : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Статус оплаты</span>
                  <span className={`${getPaymentStatus(order) !== 'Оплачено' ? 'text-red-500' : 'text-green-600'}`}>{order.status !== 'canceled' ? getPaymentStatus(order) : ''}</span>
                </div>
              </div>
              
              <Collapsible open={expandedOrder === order.orderId} onOpenChange={() => {}}>
                <CollapsibleContent className="border-t border-gray-100">
                  {order.products.length > 0 && (
                    <div className="p-4">
                      <h4 className="mb-2 font-medium">Товары в заказе:</h4>
                      <div className="space-y-2">
                        {order.products.map((item, index) => (
                          <div key={index} className="text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Артикул:</span>
                              <span>{item.article}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Бренд:</span>
                              <span>{item.brand}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Наименование:</span>
                              <span>{item.name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Цена:</span>
                              <span>{item.price} × {item.amount} шт.</span>
                            </div>
                            {index < order.products.length - 1 && <hr className="my-2 border-gray-100" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
              
              <div className="flex flex-col gap-2 p-4 border-t border-gray-100">
                <Button 
                  className="w-full bg-autoparts-orange text-white hover:bg-autoparts-orange/90"
                  style={{padding: '0 1rem'}}
                  onClick={() => document.location.href = `/m/${order.orderHash}`}
                >
                  Посмотреть
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full border-autoparts-orange text-autoparts-orange hover:bg-autoparts-orange/10"
                  style={{padding: '0 1rem'}}
                  onClick={() => copyOrder(order.orderId)}
                >
                  Повторить
                </Button>
                {order.products.length > 0 && (
                  <Button 
                    variant="ghost" 
                    className="w-full text-gray-600 flex items-center justify-center"
                    style={{padding: '0 1rem'}}
                    onClick={() => toggleOrderDetails(order.orderId)}
                  >
                    {expandedOrder === order.orderId ? (
                      <>Скрыть детали <ChevronUp className="ml-1 h-4 w-4" /></>
                    ) : (
                      <>Показать детали <ChevronDown className="ml-1 h-4 w-4" /></>
                    )}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Desktop view
        <div className="rounded-lg bg-white shadow overflow-hidden">
          <Table className="w-full border-collapse">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="px-2 py-2 text-left text-gray-500">Дата</TableHead>
                <TableHead className="px-2 py-2 text-left text-gray-500">Номер</TableHead>
                <TableHead className="px-2 py-2 text-left text-gray-500">Статус</TableHead>
                <TableHead className="px-2 py-2 text-left text-gray-500">Сумма</TableHead>
                <TableHead className="px-2 py-2 text-left text-gray-500">Статус оплаты</TableHead>
                <TableHead className="px-2 py-2"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <React.Fragment key={order.orderId}>
                  <TableRow className="border-b hover:bg-gray-50">
                    <TableCell className="px-2 py-2">{formatDate(order.createdAt)}</TableCell>
                    <TableCell className="px-2 py-2 text-autoparts-orange font-medium"><a href={`/m/${order.orderHash}`}>{order.orderNumber}</a></TableCell>
                    <TableCell className={`px-2 py-2 ${order.status === 'canceled' ? 'text-red-500' : 'text-green-600'}`}>{getStatusDescription(order, false)}</TableCell>
                    <TableCell className="px-2 py-2">{order.status !== 'canceled' ? order.priceAmount : ''}</TableCell>
                    <TableCell className={`px-2 py-2 ${getPaymentStatus(order) !== 'Оплачено' ? 'text-red-500' : 'text-green-600'}`}>{order.status !== 'canceled' ? getPaymentStatus(order) : ''}</TableCell>
                    <TableCell className="px-2 py-2 space-x-2 whitespace-nowrap">
                      <Button 
                        className="bg-autoparts-orange text-white hover:bg-autoparts-orange/90"
                        style={{padding: '0 1rem'}}
                        onClick={() => document.location.href = `/m/${order.orderHash}`}
                      >
                        Посмотреть
                      </Button>
                      <Button 
                        variant="outline"
                        className="border-autoparts-orange text-autoparts-orange hover:bg-autoparts-orange/10"
                        style={{padding: '0 1rem'}}
                        onClick={() => copyOrder(order.orderId)}
                      >
                        Повторить
                      </Button>
                      {order.products.length > 0 && (
                        <Button 
                          variant="ghost" 
                          className="text-gray-600"
                          style={{padding: '0 1rem'}}
                          onClick={() => toggleOrderDetails(order.orderId)}
                        >
                          {expandedOrder === order.orderId ? (
                            <>Скрыть детали <ChevronUp className="ml-1 h-4 w-4" /></>
                          ) : (
                            <>Показать детали <ChevronDown className="ml-1 h-4 w-4" /></>
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  
                  {expandedOrder === order.orderId && order.products.length > 0 && (
                    <TableRow className="bg-gray-50">
                      <TableCell colSpan={6} className="p-0">
                        <div className="px-2 py-2">
                          <h4 className="mb-2 font-medium">Товары в заказе:</h4>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-sm text-gray-500">Код</TableHead>
                                <TableHead className="text-sm text-gray-500">Бренд</TableHead>
                                <TableHead className="text-sm text-gray-500">Наименование</TableHead>
                                <TableHead className="text-sm text-gray-500 text-right">Стоимость</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {order.products.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell className="text-sm">{item.article}</TableCell>
                                  <TableCell className="text-sm">{item.brand}</TableCell>
                                  <TableCell className="text-sm">{item.name}</TableCell>
                                  <TableCell className="text-sm text-right">{item.price} × {item.amount} шт.</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
